import { useEffect, useState, useCallback } from 'react';
import type { FinancialRecord } from '@akfdev-com/shared';
import { financialRecordsApi } from '../../services/api';

/**
 * Hook for loading a contact's financial records via getByContact.
 * Returns records across all buildings for the contact, scoped by the resolved ownerId.
 *
 * Implements Requirement 2.1 (read owner-scoped records) and Requirement 2.6 (getByContact path)
 * and Requirement 4.5 (refetch on delete).
 */
export function useContactFinancials(contactId: string): {
  records: FinancialRecord[];
  loading: boolean;
  error: boolean;
  empty: boolean;
  refetch: () => Promise<void>;
} {
  const [records, setRecords] = useState<FinancialRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const result = await financialRecordsApi.getByContact(contactId);
      setRecords(result || []);
      setError(false);
    } catch (err) {
      console.error('[useContactFinancials] error fetching records:', err);
      setError(true);
      setRecords([]); // Clear on error per Req 2.9 (no partial/stale data)
    } finally {
      setLoading(false);
    }
  }, [contactId]);

  useEffect(() => {
    void refetch();
  }, [contactId, refetch]);

  const empty = records.length === 0 && !loading && !error;

  return {
    records,
    loading,
    error,
    empty,
    refetch,
  };
}
